import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { brandStory } from '../mock';
import { 
  Factory,
  Recycle,
  Truck,
  TrendingUp,
  Award,
  Users,
  Leaf,
  MapPin,
  Calendar,
  Target,
  ArrowRight,
  Building2,
  Globe,
  Shield,
  Handshake
} from 'lucide-react';

const BrandStory = () => {
  const storyCards = [
    {
      icon: Building2,
      title: "Heritage & Excellence",
      content: {
        ...brandStory.heritage,
        description: "Born in Dalton, Georgia - the Carpet Capital of the World - AutoMats has been setting the gold standard for quality, sustainability, and production in the U.S. since 1985. Our 40+ years of experience ensure every mat meets the highest standards of durability and performance."
      },
      image: "/images/brandstory-4.webp",
      color: "automats-red",
      bgColor: "from-red-50 to-red-100"
    },
    {
      icon: Globe,
      title: "Superior Quality Recycling", 
      content: {
        ...brandStory.sustainability,
        title: "Superior Quality from 100% Recycled Materials",
        description: "We're the only company making superior-quality, first-class mats from 100% recycled materials, with zero virgin content. Our proprietary process transforms post-industrial and post-consumer waste into premium automotive protection that exceeds industry standards. Recycled doesn't mean compromised - it means superior sustainability without sacrificing quality."
      },
      image: "/images/brandstory-8.webp",
      color: "automats-eco-green",
      bgColor: "from-green-50 to-green-100"
    },
    {
      icon: Shield,
      title: "American Manufacturing",
      content: brandStory.manufacturing,
      image: "/images/brandstory-6.webp",
      color: "automats-steel",
      bgColor: "from-gray-50 to-gray-100"
    },
    {
      icon: Handshake,
      title: "Custom Branding Solutions",
      content: {
        ...brandStory.margins,
        title: "Your Brand on Every Mat",
        description: "Turn every floor mat into a powerful marketing tool. We offer comprehensive custom branding solutions that let you put your logo, company name, and messaging directly on the mats. From simple logo placement to full custom designs with up to 5 colors, we transform floor mats into effective advertising platforms that keep your brand visible to customers every day.",
        benefits: [
          "Custom logo placement and branding",
          "Up to 5-color printing capabilities", 
          "Co-branded packaging options",
          "Transform mats into advertising tools",
          "Competitive wholesale pricing",
          "Flexible minimum order quantities"
        ]
      },
      image: "/images/brandstory-kenworth.webp",
      color: "automats-red", 
      bgColor: "from-red-50 to-red-100"
    },
    {
      icon: Factory,
      title: "Advanced Production",
      content: {
        title: "State-of-the-Art Manufacturing Process",
        description: "Our 200,000 sq ft facility houses cutting-edge equipment and proprietary processes that ensure consistent quality and efficient production. From raw material processing to final quality control, every step is optimized for excellence and environmental responsibility.",
        advantages: [
          "Fully automated production lines",
          "Advanced quality control systems",
          "In-house material formulation",
          "Proprietary manufacturing processes",
          "Real-time production monitoring",
          "Zero waste manufacturing protocols"
        ]
      },
      image: "/images/brandstory-8b.webp",
      color: "automats-steel",
      bgColor: "from-gray-50 to-gray-100"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced Hero Section */}
      <section className="bg-white py-20 overflow-hidden relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
            <defs>
              <pattern id="story-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#story-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <Badge className="bg-automats-eco-green/10 text-automats-eco-green border-automats-eco-green/20 px-6 py-2">
                Our Story
              </Badge>
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl font-heading font-bold text-automats-black leading-tight">
                  40 Years of{' '}
                  <span className="text-automats-red">Innovation</span>{' '}
                  and{' '}
                  <span className="text-automats-eco-green">Sustainability</span>
                </h1>
                <p className="text-xl font-body text-automats-steel leading-relaxed">
                  Born in Dalton, Georgia—the Carpet Capital of the World—AutoMats isn't just 
                  another aftermarket floor mat brand. We've been transforming waste into value 
                  for over four decades, setting the gold standard for quality, sustainability, 
                  and American manufacturing.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-6">
                <a 
                  href="mailto:sales@automats.us?subject=Partnership Information Request&body=Hello, I would like to learn more about partnering with AutoMats."
                  className="bg-automats-red hover:bg-red-700 text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  Request Partnership Info
                </a>
              </div>
            </div>

            {/* Enhanced Timeline Visual */}
            <div className="bg-gradient-to-br from-automats-off-white to-white rounded-3xl p-10 shadow-2xl border border-gray-100">
              <h3 className="text-3xl font-heading font-bold mb-8 text-center text-automats-black">Key Milestones</h3>
              <div className="space-y-8">
                <div className="flex items-center space-x-6">
                  <div className="bg-automats-red w-16 h-16 rounded-full flex items-center justify-center font-heading font-bold text-white text-lg shadow-lg">
                    1985
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-automats-black text-lg">Company Founded</div>
                    <div className="text-automats-steel font-body">Started in Dalton, Georgia - Carpet Capital of the World</div>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="bg-automats-eco-green w-16 h-16 rounded-full flex items-center justify-center font-heading font-bold text-white text-lg shadow-lg">
                    2010
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-automats-black text-lg">100% Recycled Materials</div>
                    <div className="text-automats-steel font-body">Industry-first zero virgin content manufacturing</div>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="bg-automats-steel w-16 h-16 rounded-full flex items-center justify-center font-heading font-bold text-white text-lg shadow-lg">
                    2020
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-automats-black text-lg">200,000 Sq Ft Expansion</div>
                    <div className="text-automats-steel font-body">Doubled production capacity and efficiency</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Story Cards */}
        <div className="space-y-20">
          {storyCards.map((story, index) => {
            const IconComponent = story.icon;
            return (
              <Card key={index} className="overflow-hidden shadow-2xl border border-gray-100">
                <div className="grid lg:grid-cols-2 min-h-[500px]">
                  {index % 2 === 0 ? (
                    <>
                      {/* Content Left */}
                      <CardContent className="p-12 flex flex-col justify-center">
                        <div className="space-y-8">
                          <div className="flex items-center space-x-6">
                            <div className={`p-4 rounded-full bg-gradient-to-br ${story.bgColor} shadow-lg`}>
                              <IconComponent className={`h-10 w-10 text-${story.color}`} />
                            </div>
                            <h2 className="text-4xl font-heading font-bold text-automats-black">
                              {story.title}
                            </h2>
                          </div>
                          
                          <p className="text-xl font-body text-automats-steel leading-relaxed">
                            {story.content.description}
                          </p>

                          {story.content.stats && (
                            <div className="grid grid-cols-2 gap-6">
                              {story.content.stats.map((stat, statIndex) => (
                                <div key={statIndex} className="text-center p-6 bg-automats-off-white rounded-xl">
                                  <div className={`text-4xl font-heading font-bold text-${story.color} mb-2`}>
                                    {stat.value}
                                  </div>
                                  <div className="text-sm font-body text-automats-steel">
                                    {stat.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {(story.content.benefits || story.content.advantages) && (
                            <ul className="space-y-4">
                              {(story.content.benefits || story.content.advantages).map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start space-x-4">
                                  <ArrowRight className={`h-6 w-6 text-${story.color} mt-1 flex-shrink-0`} />
                                  <span className="text-lg font-body text-automats-steel">{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </CardContent>

                      {/* Visual Right - Manufacturing Image */}
                      <div className={`bg-gradient-to-br ${story.bgColor} flex items-center justify-center p-8`}>
                        <div className="w-full max-w-lg mx-auto">
                          <img 
                            src={story.image}
                            alt={story.title}
                            className="w-full h-auto rounded-2xl shadow-2xl object-cover"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Visual Left - Manufacturing Image */}
                      <div className={`bg-gradient-to-br ${story.bgColor} flex items-center justify-center p-8`}>
                        <div className="w-full max-w-lg mx-auto">
                          <img 
                            src={story.image}
                            alt={story.title}
                            className="w-full h-auto rounded-2xl shadow-2xl object-cover"
                          />
                        </div>
                      </div>

                      {/* Content Right */}
                      <CardContent className="p-12 flex flex-col justify-center">
                        <div className="space-y-8">
                          <div className="flex items-center space-x-6">
                            <div className={`p-4 rounded-full bg-gradient-to-br ${story.bgColor} shadow-lg`}>
                              <IconComponent className={`h-10 w-10 text-${story.color}`} />
                            </div>
                            <h2 className="text-4xl font-heading font-bold text-automats-black">
                              {story.title}
                            </h2>
                          </div>
                          
                          <p className="text-xl font-body text-automats-steel leading-relaxed">
                            {story.content.description}
                          </p>

                          {(story.content.benefits || story.content.advantages) && (
                            <ul className="space-y-4">
                              {(story.content.benefits || story.content.advantages).map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start space-x-4">
                                  <ArrowRight className={`h-6 w-6 text-${story.color} mt-1 flex-shrink-0`} />
                                  <span className="text-lg font-body text-automats-steel">{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </CardContent>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Enhanced Values Section */}
        <section className="mt-20 bg-white rounded-3xl p-12 shadow-2xl border border-gray-100">
          <div className="text-center mb-12">
            <Badge className="bg-automats-red/10 text-automats-red border-automats-red/20 mb-6">
              Core Values
            </Badge>
            <h2 className="text-4xl font-heading font-bold text-automats-black mb-6">Our Core Values</h2>
            <p className="text-xl font-body text-automats-steel max-w-3xl mx-auto leading-relaxed">
              These principles guide everything we do, from manufacturing to partnerships
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center space-y-6">
              <div className="bg-automats-red w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-automats-black">Excellence</h3>
              <p className="text-lg font-body text-automats-steel leading-relaxed">
                We never compromise on quality. Every mat meets our exacting standards for durability and performance.
              </p>
            </div>
            <div className="text-center space-y-6">
              <div className="bg-automats-eco-green w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Leaf className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-automats-black">Superior Sustainability</h3>
              <p className="text-lg font-body text-automats-steel leading-relaxed">
                Environmental responsibility meets superior quality. Our 100% recycled materials produce first-class, premium mats that exceed industry standards—proving recycled means better, not compromised.
              </p>
            </div>
            <div className="text-center space-y-6">
              <div className="bg-automats-steel w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-automats-black">Partnership</h3>
              <p className="text-lg font-body text-automats-steel leading-relaxed">
                Your success is our success. We build lasting relationships, not just transactions.
              </p>
            </div>
            <div className="text-center space-y-6">
              <div className="bg-automats-red w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Target className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-automats-black">Custom Branding</h3>
              <p className="text-lg font-body text-automats-steel leading-relaxed">
                Transform floor mats into powerful marketing tools. Every mat becomes an advertisement for your business with custom logo placement and branding options.
              </p>
            </div>
          </div>
        </section>

        {/* Enhanced CTA Section */}
        <section className="mt-20 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl font-heading font-bold text-automats-black">
              Ready to Be Part of Our Story?
            </h2>
            <p className="text-xl font-body text-automats-steel max-w-3xl mx-auto leading-relaxed">
              Join hundreds of dealers who've discovered the AutoMats advantage. 
              Let's write the next chapter together.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a 
                href="mailto:sales@automats.us?subject=Partnership Opportunity&body=Hello, I would like to explore becoming an AutoMats partner."
                className="bg-automats-red hover:bg-red-700 text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Become a Partner
              </a>
              <a 
                href="mailto:sales@automats.us?subject=Schedule a Call&body=Hello, I would like to schedule a call to discuss AutoMats products and partnership opportunities."
                className="border-2 border-automats-steel text-automats-steel hover:bg-automats-steel hover:text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105"
              >
                Schedule a Call
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BrandStory;